import { createReducer, on } from '@ngrx/store';
import * as UserActions from './user.actions';
import { User } from '../../../shared/models/user.model';

export interface LinkedAccount {
  provider: string;
  created_at: string;
}

export interface UserState {
  user: User | null;
  loading: boolean;
  error: any;
  allUsers: User[];
  userCount: number;
  linkedAccounts: LinkedAccount[];
  linkedAccountsLoading: boolean;
}

const initialState: UserState = {
  user: null,
  loading: false,
  error: null,
  allUsers: [],
  userCount: 0,
  linkedAccounts: [],
  linkedAccountsLoading: false,
};

export const userReducer = createReducer(
  initialState,
  on(UserActions.loadUser, (state) => ({
    ...state,
    loading: true,
  })),
  on(UserActions.loadUserSuccess, (state, { user }) => ({
    ...state,
    loading: false,
    user,
  })),
  on(UserActions.loadUserFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(UserActions.updateUserPreferences, (state) => ({
    ...state,
    loading: true,
  })),
  on(UserActions.updateUserPreferencesSuccess, (state, { updatedPreferences }) => ({
    ...state,
    loading: false,
    user: state.user
      ? {
          ...state.user,
          ...updatedPreferences,
          ...(updatedPreferences.preferred_timezone !== undefined
            ? { preferred_timezone: updatedPreferences.preferred_timezone }
            : {})
        }
      : null
  })),
  on(UserActions.updateUserPreferencesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(UserActions.updateUserProfile, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(UserActions.updateUserProfileSuccess, (state, { user }) => ({
    ...state,
    user: state.user
      ? {
          ...state.user,
          name: user?.name ?? state.user.name,
        }
      : state.user,
    loading: false,
  })),
  on(UserActions.updateUserProfileFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(UserActions.updateUserEmail, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(UserActions.updateUserEmailSuccess, (state, { user }) => ({
    ...state,
    user,
    loading: false
  })),
  on(UserActions.updateUserEmailFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(UserActions.updateUserPassword, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(UserActions.updateUserPasswordSuccess, (state) => ({
    ...state,
    loading: false
  })),
  on(UserActions.updateUserPasswordFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(UserActions.deleteUserAccount, (state) => ({
    ...state,
    loading: true,
  })),
  on(UserActions.deleteUserAccountSuccess, (state) => ({
    ...state,
    loading: false
  })),
  on(UserActions.deleteUserAccountFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),
  on(UserActions.updateUserOnboardingStatus, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(UserActions.updateUserOnboardingStatusSuccess, (state, { onboarding_complete }) => ({
    ...state,
    loading: false,
    user: state.user ? { ...state.user, onboarding_complete } : state.user
  })),
  on(UserActions.updateUserOnboardingStatusFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(UserActions.updateUserRole, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(UserActions.updateUserRoleSuccess, (state, { username, role }) => ({
    ...state,
    loading: false,
    allUsers: state.allUsers.map(user =>
      user.username === username ? { ...user, role } : user
    )
  })),
  on(UserActions.updateUserRoleFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(UserActions.loadUserCount, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(UserActions.loadUserCountSuccess, (state, { count }) => ({
    ...state,
    loading: false,
    userCount: count
  })),
  on(UserActions.loadUserCountFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  on(UserActions.loadAllUsers, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(UserActions.loadAllUsersSuccess, (state, { users }) => ({
    ...state,
    loading: false,
    allUsers: users
  })),
  on(UserActions.loadAllUsersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(UserActions.updateMarketplaceSubscription, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(UserActions.updateMarketplaceSubscriptionSuccess, (state, { marketplace_subscribed, marketplace_subscribed_at }) => ({
    ...state,
    loading: false,
    user: state.user ? { ...state.user, marketplace_subscribed, marketplace_subscribed_at } : state.user
  })),
  on(UserActions.updateMarketplaceSubscriptionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(UserActions.loadLinkedAccounts, (state) => ({
    ...state,
    linkedAccountsLoading: true,
  })),
  on(UserActions.loadLinkedAccountsSuccess, (state, { accounts }) => ({
    ...state,
    linkedAccountsLoading: false,
    linkedAccounts: accounts,
  })),
  on(UserActions.loadLinkedAccountsFailure, (state, { error }) => ({
    ...state,
    linkedAccountsLoading: false,
    error,
  })),
  on(UserActions.clearUser, () => initialState)
);
